import { Button, Dialog, DialogContent, Box, Stack, Typography, Tooltip } from '@mui/material';
import SvgIconStyle from '@components/SvgIconStyle';
import { FC } from 'react';
import useLocales from '@hooks/useLocales';
import { COLORS } from '../constants';
import { styles } from './styles';
import DialogTitle from '../dialog-title';
import Color from './color';
import { FormProvider, RHFSwitch, RHFTextField } from '~/components/hook-form';
import { UseFormReturn } from 'react-hook-form';
import LeavePageDialog from './leave-page-dialog';
import { ListFormValues } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  onClickBack?: () => void;
  isOpenLeavePage: boolean;
  onCancel: () => void;
  onClickLeaveAnyway: () => void;
  onSubmit: () => void;
  onReset: () => void;
  methods: UseFormReturn<ListFormValues>;
  dialogTitle: string;
  submitButtonTitle: string;
  creatableListType: string;
  selectedIds?: string[];
};

const CreateOrEditListDialog: FC<Props> = (props) => {
  const {
    open,
    onClose,
    onClickBack,
    isOpenLeavePage,
    onCancel,
    onClickLeaveAnyway,
    onSubmit,
    methods,
    onReset,
    dialogTitle,
    submitButtonTitle,
    creatableListType,
    selectedIds,
  } = props;
  const { t } = useLocales();

  return (
    <>
      <Dialog onClose={onClose} open={open}>
        <DialogTitle
          onClose={onClose}
          title={t(`dashboard.search.results.create_edit_list.${dialogTitle}`)}
        />
        <DialogContent dividers sx={styles.dialogContent}>
          <FormProvider
            methods={methods}
            onSubmit={methods.handleSubmit(() => {
              onSubmit();
              // onClickLeaveAnyway cleans up selectedIds,
              // but in case of Opportunities we need them in the following review credits pop-ups
              if (
                creatableListType != 'OpportunityList' ||
                selectedIds === undefined ||
                selectedIds.length === 0
              )
                onClickLeaveAnyway();
            })}
          >
            <Stack direction="row" justifyContent="space-between">
              {COLORS.map((color) => (
                <Color
                  key={color}
                  color={color}
                  activeColor={methods.watch('color')}
                  setActiveColor={() => methods.setValue('color', color)}
                />
              ))}
            </Stack>
            <RHFTextField
              name="name"
              size="small"
              label={t('dashboard.search.results.create_edit_list.name')}
              sx={styles.textField}
              required={true}
            />
            <RHFTextField
              name="description"
              label={t('dashboard.search.results.create_edit_list.description')}
              multiline
              rows={5}
              sx={styles.textField}
            />
            <Box mt="15px">
              <RHFSwitch
                name="synced"
                label={
                  <Box sx={styles.switchTextContainer}>
                    <Typography variant="body2">
                      {t('dashboard.search.results.create_edit_list.synced_with_bpd')}
                    </Typography>
                    <SvgIconStyle
                      src="/icons/bpd.svg"
                      sx={methods.watch('synced') ? styles.switchActiveIcon : styles.switchIcon}
                    />
                  </Box>
                }
              />
            </Box>
            <Box>
              <RHFSwitch
                name="public"
                label={
                  <Tooltip
                    title={t('dashboard.search.results.create_edit_list.public_tooltip') as string}
                    arrow
                    placement="top"
                  >
                    <Box sx={styles.switchTextContainer}>
                      <Typography variant="body2">
                        {t('dashboard.search.results.create_edit_list.public')}
                      </Typography>
                      <SvgIconStyle
                        src="/icons/share/24/outline.svg"
                        sx={methods.watch('public') ? styles.switchActiveIcon : styles.switchIcon}
                      />
                    </Box>
                  </Tooltip>
                }
              />
            </Box>
            <Stack direction="row" sx={styles.footer}>
              <Box>
                {!!onClickBack && (
                  <Button
                    variant="outlined"
                    size="medium"
                    color="inherit"
                    startIcon={
                      <SvgIconStyle
                        src="/icons/iconly/light/arrow_left_square.svg"
                        sx={styles.icon}
                      />
                    }
                    onClick={onClickBack}
                  >
                    {t('dashboard.search.results.create_edit_list.back')}
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing="16px">
                <Button variant="text" size="medium" color="inherit" onClick={onClose}>
                  {t('dashboard.search.results.create_edit_list.cancel')}
                </Button>
                <Button type="submit" variant="contained" size="medium" color="primary">
                  {t(`dashboard.search.results.create_edit_list.${submitButtonTitle}`)}
                </Button>
              </Stack>
            </Stack>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <LeavePageDialog
        title={t('dashboard.search.results.leave_page.title')}
        description={t('dashboard.search.results.leave_page.description')}
        button_text={t('dashboard.search.results.leave_page.leave_anyway')}
        open={isOpenLeavePage}
        onCancel={onCancel}
        onClickLeaveAnyway={() => {
          onClickLeaveAnyway();
          onReset();
        }}
      />
    </>
  );
};

export default CreateOrEditListDialog;
